import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { X, Loader2, IndianRupee, IdCard as IdIcon, Upload } from "lucide-react";

// All supported ID templates. Each has a distinct field-label mapping and header color.
const TEMPLATES = [
  { key: "aadhaar",         name: "Aadhaar Card",       tone: "from-orange-600 to-red-800",   defaultOrg: "GOVT. OF INDIA",
    labels: { name: "Full Name", id_no: "Aadhaar Number", role: "DOB", phone: "Mobile" } },
  { key: "pan",             name: "PAN Card",           tone: "from-blue-800 to-indigo-700",  defaultOrg: "INCOME TAX DEPT",
    labels: { name: "Full Name", id_no: "PAN Number",     role: "Father's Name", phone: "DOB" } },
  { key: "driving_licence", name: "Driving Licence",    tone: "from-emerald-700 to-green-500", defaultOrg: "RTO",
    labels: { name: "Full Name", id_no: "DL Number",      role: "Vehicle Class", phone: "Valid Until" } },
  { key: "voter",           name: "Voter ID",           tone: "from-red-700 to-red-500",       defaultOrg: "ELECTION COMMISSION",
    labels: { name: "Full Name", id_no: "EPIC Number",    role: "Constituency", phone: "DOB" } },
  { key: "rc",              name: "Vehicle RC",         tone: "from-indigo-700 to-blue-700",   defaultOrg: "MOTOR VEHICLES DEPT",
    labels: { name: "Owner Name", id_no: "Reg Number",   role: "Vehicle Model", phone: "Valid Until" } },
  { key: "pvc",             name: "PVC ID Card",        tone: "from-slate-900 to-slate-700",   defaultOrg: "MaaPrint",
    labels: { name: "Full Name", id_no: "ID Number",      role: "Designation", phone: "Mobile" } },
  { key: "employee",        name: "Employee ID",        tone: "from-brand-blue to-brand-green", defaultOrg: "MaaPrint",
    labels: { name: "Full Name", id_no: "Employee ID",    role: "Role", phone: "Phone" } },
  { key: "student",         name: "Student ID",         tone: "from-violet-600 to-blue-600",   defaultOrg: "College Name",
    labels: { name: "Full Name", id_no: "Roll Number",    role: "Class / Dept", phone: "Phone" } },
  { key: "shop",            name: "Shop Pass",          tone: "from-orange-600 to-amber-500",  defaultOrg: "Shop Name",
    labels: { name: "Full Name", id_no: "Shop ID",        role: "Type",         phone: "Phone" } },
];

export default function IdCard() {
  const { user, refreshMe } = useAuth();
  const { t } = useI18n();
  const [templateKey, setTemplateKey] = useState("employee");
  const template = TEMPLATES.find((t2) => t2.key === templateKey);
  const [form, setForm] = useState({
    name: "", id_no: "", role: "", org: template.defaultOrg, phone: "",
  });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [printerId, setPrinterId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/printers");
      setPrinters(data);
      const def = data.find((p) => p.is_default) || data[0];
      if (def) setPrinterId(def.id);
    })();
  }, []);

  useEffect(() => {
    // Reset default org when template changes if user hasn't customized
    setForm((f) => ({ ...f, org: template.defaultOrg }));
  }, [templateKey, template.defaultOrg]);

  const handlePhoto = (f) => {
    if (!/(jpe?g|png)$/i.test(f.name)) return toast.error("JPG or PNG only");
    setPhoto(f);
    const r = new FileReader();
    r.onload = (e) => setPreview(e.target.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("fields_name", form.name);
      fd.append("fields_id_no", form.id_no);
      fd.append("fields_role", form.role);
      fd.append("fields_org", form.org);
      fd.append("fields_phone", form.phone);
      fd.append("template", templateKey);
      if (photo) fd.append("photo", photo);
      if (printerId) fd.append("printer_id", printerId);
      const { data } = await api.post("/orders/id-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${template.name} queued · ₹${data.cost}`);
      await refreshMe();
      setForm((f) => ({ ...f, name: "", id_no: "", role: "", phone: "" }));
      setPhoto(null); setPreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="id-card-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">ID Card Studio</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">ID Card Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Design and print ISO ID-1 (85.6 × 54 mm) cards — Aadhaar, PAN, DL, Voter, RC, PVC, Employee & Student templates.
        </p>
      </div>

      {/* Template picker gallery */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="template-gallery">
        {TEMPLATES.map((tp) => (
          <button
            key={tp.key}
            onClick={() => setTemplateKey(tp.key)}
            data-testid={`template-${tp.key}`}
            className={`group aspect-[85.6/54] rounded-lg overflow-hidden border transition-all
              ${templateKey === tp.key
                ? "border-primary ring-2 ring-primary/30 shadow-lg"
                : "border-border hover:border-primary/40"}`}
          >
            <div className={`h-1/3 bg-gradient-to-br ${tp.tone}`} />
            <div className="h-2/3 bg-white flex items-center justify-center px-1">
              <p className="text-[9px] font-semibold text-slate-800 text-center leading-tight">{tp.name}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Live preview */}
        <Card className="lg:col-span-2 p-6 border-border bg-card">
          <div className="aspect-[85.6/54] rounded-xl border border-border bg-white overflow-hidden shadow-md">
            <div className={`h-[24%] bg-gradient-to-br ${template.tone} px-4 flex items-end pb-2`}>
              <div>
                <p className="text-white text-xs font-bold uppercase tracking-widest">{form.org || template.defaultOrg}</p>
                <p className="text-white/85 text-[10px] uppercase tracking-wide">{template.name}</p>
              </div>
            </div>
            <div className="p-4 flex gap-3 h-[76%]">
              <div className="w-24 h-24 rounded-md border border-border overflow-hidden bg-secondary flex items-center justify-center flex-shrink-0">
                {preview
                  ? <img src={preview} alt="" className="w-full h-full object-cover" />
                  : <IdIcon className="text-muted-foreground" size={24} />}
              </div>
              <div className="min-w-0 flex-1 text-slate-800">
                <p className="font-bold text-sm truncate">{form.name || template.labels.name}</p>
                {form.id_no && <p className="text-[10px] text-slate-500 mt-1"><b>{template.labels.id_no}:</b> {form.id_no}</p>}
                {form.role && <p className="text-[10px] text-slate-500"><b>{template.labels.role}:</b> {form.role}</p>}
                {form.phone && <p className="text-[10px] text-slate-500"><b>{template.labels.phone}:</b> {form.phone}</p>}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-center text-muted-foreground mt-3">Live preview · ISO ID-1</p>
        </Card>

        {/* Form */}
        <Card className="lg:col-span-3 p-6 border-border bg-card space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>{template.labels.name}*</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5" data-testid="idcard-name-input" />
            </div>
            <div>
              <Label>{template.labels.id_no}</Label>
              <Input value={form.id_no} onChange={(e) => setForm({ ...form, id_no: e.target.value })}
                className="mt-1.5" data-testid="idcard-idno-input" />
            </div>
            <div>
              <Label>{template.labels.role}</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1.5" data-testid="idcard-role-input" />
            </div>
            <div>
              <Label>Organisation</Label>
              <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })}
                className="mt-1.5" data-testid="idcard-org-input" />
            </div>
            <div>
              <Label>{template.labels.phone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1.5" data-testid="idcard-phone-input" />
            </div>
            <div className="col-span-2">
              <Label>Printer</Label>
              <Select value={printerId} onValueChange={setPrinterId}>
                <SelectTrigger className="mt-1.5" data-testid="idcard-printer-select">
                  <SelectValue placeholder="Select printer" />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-3 rounded-lg border border-dashed border-border">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Photo (optional)</Label>
            {photo ? (
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm truncate flex-1">{photo.name}</span>
                <Button variant="ghost" size="icon" onClick={() => { setPhoto(null); setPreview(null); }}>
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <label className="mt-2 inline-flex items-center gap-2 text-sm text-primary cursor-pointer hover:underline">
                <Upload size={14} /> Upload photo
                <input type="file" accept="image/jpeg,image/png" className="hidden"
                  onChange={(e) => e.target.files?.[0] && handlePhoto(e.target.files[0])}
                  data-testid="idcard-photo-input" />
              </label>
            )}
          </div>

          <Button
            onClick={submit}
            disabled={submitting || !form.name.trim() || !printerId}
            className="w-full h-11 bg-primary hover:bg-brand-blue-hover"
            data-testid="idcard-submit-btn"
          >
            {submitting
              ? <><Loader2 className="animate-spin mr-2" size={16} /> {t("print.sending")}</>
              : <><IndianRupee size={14} className="mr-1" /> Print {template.name}</>}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            Wallet balance: ₹{Number(user.wallet_balance || 0).toFixed(2)}
          </p>
        </Card>
      </div>
    </div>
  );
}
